import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { Formik } from 'formik';
import { hook } from '@g4/automation-hooks';
import tracking from '@g4/g4-analytics';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { isEmpty } from 'lodash';
import { useTranslations } from '../../../graphql/components/ApplicationQueryProvider';
import { useSSRQuery } from '../../../graphql';
import NEARBY_FLIGHT_LOCATIONS_QUERY from '../../../pageComponents/indexPage/wrappedFlightSearchForm/nearbyFlightLocations.query.graphql';
import {
  Button,
  Col,
  Row,
  VerticalSpacing,
  Box,
  Text,
  Media,
} from '../../../components/Boneyard';
import Markdown from '../../../components/Markdown';
import FormField from '../../../components/FormField';
import { Spinner } from '../../../components/Boneyard';
import Asterisk from '@g4/next-furniture/LoginSignup/Asterisk';
import Input from '../../../components/boneyard/Input';
import Select from '@g4/next-furniture/FlightSearchForm/flightSearchForm/Select/Select';
import {
  EMAIL_SIGN_UP_FORM,
  EMAIL_SIGN_UP_CLOSE_BUTTON,
  EMAIL_SIGN_UP_EMAIL_INPUT,
  EMAIL_SIGN_UP_GET_DEALS_BUTTON,
  EMAIL_SIGN_UP_DEAL,
  EMAIL_SIGN_UP_DESCRIPTION,
  EMAIL_SIGN_UP_TITLE,
  EMAIL_SIGN_UP_EMAIL_ERROR,
  EMAIL_SIGN_UP_SUCCESS_MESSAGE,
  EMAIL_SIGN_UP_AIRPORT,
} from '../../../../automationHooks';
import * as Yup from 'yup';

import CLOSE_ICON from '../../../images/ic-close-white.svg';
import CHECKMARK_ICON from '../../../images/ic-checkmark-blue.svg';

const CloseIcon = styled.button`
  width: 17px;
  height: 17px;
  margin-right: ${props => props.theme.spacing(2)};
`;

const CheckmarkIcon = styled.div`
  width: 21px;
  height: 16px;
  background-image: url(${CHECKMARK_ICON});
  background-size: cover;
  margin-right: ${props => props.theme.spacing(2)};
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing(4)};
  border-bottom: 8px solid ${props => props.theme.colors.emailSignUp.border};
  background-color: ${props => props.theme.colors.emailSignUp.stripBackground};
  border-radius: 8px 8px 0 0;
  text-align: center;
  ${props => props.theme.query.md} {
    width: 744px;
    border-radius: 8px 0 0 0;
    padding: ${props => props.theme.spacing(3)};
  }
`;

const Body = styled.div`
  background-color: ${props => props.theme.colors.emailSignUp.formBackground};
  border-radius: 0 0 0 8px;
  padding: ${props => props.theme.spacing(4)} ${props => props.theme.spacing(8)};
  ${props => props.theme.query.md} {
    display: flex;
    padding: ${props => props.theme.spacing(4)};
    padding-left: ${props => props.theme.spacing(8)};
  }
`;

const Form = styled.form`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  ${props => props.theme.query.md} {
    width: 44%;
  }
`;

const FieldErrorMessage = styled(Text)`
  position: absolute;
  color: ${props => props.theme.colors.emailSignUp.errorMessage};
  font-style: italic;
  bottom: -15px;
`;

const EmailSubscriptionSuccess = styled(Text)`
  position: absolute;
  color: ${props => props.theme.colors.emailSignUp.successMessage};
  font-style: italic;
  top: -20px;
`;

const EmailInput = styled(Input)`
  text-align: center;
  text-overflow: initial;
  &::placeholder {
    opacity: ${props => (props.opacity ? props.opacity : 1)};
  }
`;

const StyledLabel = styled.label`
  font-size: 14px;
  display: block;
  margin-bottom: ${props => props.theme.spacing(2)};
`;

const SubmitButton = styled(Button)`
  span {
    z-index: 0;
  }
`;

const EmailSignUpCard = ({ theme, handleClick }) => {
  const initialDataResponse = useSSRQuery(NEARBY_FLIGHT_LOCATIONS_QUERY);
  const translations = useTranslations();

  const locations = initialDataResponse?.data?.flightLocations;

  const airportList = locations?.map(item => {
    return { value: item.airport.code, label: item.name };
  });

  const {
    titleText,
    emailInputPlaceholder,
    getDealsButtonLabel,
    descriptionRegularText,
    descriptionBoldText,
    listItem1,
    listItem2,
    listItem3,
    listItem4,
    listItem5,
    emailInvalidFormatError,
    emailAlreadyRegisteredError,
    emailUnknownError,
    emailSubscriptionSuccess,
    selectHomeAirportInputPlaceholder,
    emailInputLabel,
    selectHomeAirportInputLabel,
  } = translations.emailSignUp;

  const getValidationSchema = translations => {
    const {
      emailInvalidFormatError,
      emailRequiredError,
      selectHomeAirportFieldError,
    } = translations.emailSignUp;

    return Yup.object().shape({
      email: Yup.string()
        .email(emailInvalidFormatError)
        .required(emailRequiredError),
      homeAirport: Yup.string().required(selectHomeAirportFieldError),
    });
  };

  const errorTypes = {
    EMAIL_IN_USE: emailAlreadyRegisteredError,
    INVALID_FORMAT: emailInvalidFormatError,
    UNKNOWN_ERROR: emailUnknownError,
  };

  const validationSchema = getValidationSchema(translations);

  const SUBMIT_EMAIL = gql`
    mutation submitEmailNewsletter(
      $email: EmailAddress!
      $homeAirport: IataCode
    ) {
      submitEmailNewsletter(email: $email, homeAirport: $homeAirport) {
        error
      }
    }
  `;

  const [submitEmail] = useMutation(SUBMIT_EMAIL);
  const [submissionAttempted, setSubmissionAttempted] = React.useState(false);

  const submitHandler = (
    values,
    { setSubmitting, setFieldError, resetForm }
  ) => {
    setSubmissionAttempted(true);
    tracking.trackFormSubmit(
      'Travel Deals Email Signup',
      'Get Deals',
      'By Email'
    );
    const { email, homeAirport } = values;
    validationSchema.isValid(values).then(valid => {
      setSubmitting(false);
      if (valid) {
        submitEmail({ variables: { email, homeAirport: homeAirport.value } })
          .then(data => {
            if (data.submitEmailNewsletter.error) {
              setFieldError(
                'email',
                errorTypes[data.submitEmailNewsletter.error]
              );
            } else {
              resetForm();
            }
          })
          .catch(errors => {
            errors.length &&
              setFieldError('email', errorTypes['UNKNOWN_ERROR']);
          });
      }
    });
  };

  return (
    <Media>
      {mq => (
        <Box mt={mq.gte('md') ? 0 : 5} mb={mq.gte('md') ? 0 : 6}>
          <Title onClick={handleClick}>
            {mq.gte('md') && (
              <CloseIcon
                type="button"
                onClick={handleClick}
                {...hook(EMAIL_SIGN_UP_CLOSE_BUTTON.hook)}
              >
                <img alt="close popup" src={CLOSE_ICON} />
              </CloseIcon>
            )}
            <Text
              size={20}
              weight={500}
              color={theme.colors.emailSignUp.titleColor}
              automationHook={EMAIL_SIGN_UP_TITLE.hook}
            >
              {titleText}
            </Text>
          </Title>
          <Body>
            <Box w={mq.gte('md') ? '60%' : '100%'}>
              <Box mb={3}>
                <Text
                  size={14}
                  color={theme.colors.emailSignUp.textColor}
                  automationHook={EMAIL_SIGN_UP_DESCRIPTION.hook}
                >
                  <Markdown
                    source={descriptionRegularText}
                    data={{
                      boldText: `<strong>${descriptionBoldText}</strong>`,
                    }}
                  />
                </Text>
              </Box>
              <Box d="flex" alignItems="center" mb={4}>
                <CheckmarkIcon />
                <Text
                  size={14}
                  color={theme.colors.emailSignUp.textColor}
                  automationHook={`${EMAIL_SIGN_UP_DEAL.hook}-1`}
                >
                  {listItem1}
                </Text>
              </Box>
              <Box d="flex" alignItems="center" mb={4}>
                <CheckmarkIcon />
                <Text
                  size={14}
                  color={theme.colors.emailSignUp.textColor}
                  automationHook={`${EMAIL_SIGN_UP_DEAL.hook}-2`}
                >
                  {listItem2}
                </Text>
              </Box>
              <Box d="flex" alignItems="center" mb={4}>
                <CheckmarkIcon />
                <Text
                  size={14}
                  color={theme.colors.emailSignUp.textColor}
                  automationHook={`${EMAIL_SIGN_UP_DEAL.hook}-3`}
                >
                  {listItem3}
                </Text>
              </Box>
              <Box d="flex" alignItems="center" mb={4}>
                <CheckmarkIcon />
                <Text
                  size={14}
                  color={theme.colors.emailSignUp.textColor}
                  automationHook={`${EMAIL_SIGN_UP_DEAL.hook}-4`}
                >
                  {listItem4}
                </Text>
              </Box>
              <Box d="flex" alignItems="center" mb={mq.gte('md') ? 0 : 10}>
                <CheckmarkIcon />
                <Text
                  size={14}
                  color={theme.colors.emailSignUp.textColor}
                  automationHook={`${EMAIL_SIGN_UP_DEAL.hook}-5`}
                >
                  {listItem5}
                </Text>
              </Box>
            </Box>
            <Formik
              onSubmit={submitHandler}
              initialValues={{ email: '', homeAirport: '' }}
              validationSchema={validationSchema}
              validateOnChange={false}
            >
              {({ values, errors, touched, handleSubmit, isSubmitting }) => (
                <Form
                  onSubmit={handleSubmit}
                  {...hook(EMAIL_SIGN_UP_FORM.hook)}
                  noValidate
                  validateOnBlur={false}
                >
                  <Row>
                    {isEmpty(errors) &&
                      values.email &&
                      values.homeAirport &&
                      submissionAttempted && (
                        <EmailSubscriptionSuccess
                          size={12}
                          automationHook={EMAIL_SIGN_UP_SUCCESS_MESSAGE.hook}
                        >
                          {emailSubscriptionSuccess}
                        </EmailSubscriptionSuccess>
                      )}
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Box position="relative">
                        {errors.email && touched.email && (
                          <FieldErrorMessage
                            size={12}
                            automationHook={EMAIL_SIGN_UP_EMAIL_ERROR.hook}
                          >
                            {errors.email}
                          </FieldErrorMessage>
                        )}
                        <StyledLabel for="email">
                          {emailInputLabel}
                          <Asterisk>*</Asterisk>
                        </StyledLabel>
                        <FormField
                          component={EmailInput}
                          name="email"
                          automationHook={EMAIL_SIGN_UP_EMAIL_INPUT.hook}
                          type="email"
                          value={values.email}
                          placeholder={emailInputPlaceholder}
                          onChange={() =>
                            submissionAttempted && setSubmissionAttempted(false)
                          }
                        />
                      </Box>
                    </Col>
                  </Row>
                  <VerticalSpacing size={3} />

                  <Row>
                    <Col span={24}>
                      <Box position="relative">
                        {errors.homeAirport && touched.homeAirport && (
                          <FieldErrorMessage
                            size={12}
                            automationHook={EMAIL_SIGN_UP_AIRPORT.hook}
                          >
                            {errors.homeAirport}
                          </FieldErrorMessage>
                        )}
                        <StyledLabel for="homeAirport">
                          {selectHomeAirportInputLabel}
                          <Asterisk>*</Asterisk>
                        </StyledLabel>
                        <FormField
                          component={Select}
                          options={airportList}
                          name="homeAirport"
                          isSearchable
                          automationHook={EMAIL_SIGN_UP_AIRPORT.hook}
                          placeholder={selectHomeAirportInputPlaceholder}
                          ariaLabel={values.homeAirport.label}
                        />
                      </Box>
                    </Col>
                  </Row>

                  <VerticalSpacing size={3} />
                  <Row>
                    <Col span={24}>
                      <SubmitButton
                        disabled={isSubmitting}
                        htmlType="submit"
                        automationHook={EMAIL_SIGN_UP_GET_DEALS_BUTTON.hook}
                        fullWidth
                      >
                        {isSubmitting ? (
                          <Spinner size="xsmall" appearance="light" />
                        ) : (
                          getDealsButtonLabel
                        )}
                      </SubmitButton>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </Body>
        </Box>
      )}
    </Media>
  );
};

EmailSignUpCard.propTypes = {
  theme: PropTypes.object.isRequired,
  handleClick: PropTypes.func,
};

EmailSignUpCard.defaultProps = {
  handleClick: Function.prototype,
};

export default withTheme(EmailSignUpCard);
